export type Severity = 'critical' | 'error' | 'warning' | 'info' | 'debug'

export type ErrorMetadata = {
  operationName?: string
  feature?: string
  grpcStatusCode?: string
  httpStatusCode?: number
  originalError?: Error | unknown
  userMessage?: string
  handled?: boolean
  transient?: boolean
} & Record<string, unknown>

export class ApplicationError extends Error {
  metadata: ErrorMetadata | null

  readonly timestamp: Date
  private _severity: Severity
  private _feature?: string
  private _handled: boolean

  constructor(message: string, severity: Severity, metadata?: ErrorMetadata) {
    super(message)
    this.name = 'ApplicationError'
    this.timestamp = new Date()
    this._severity = severity
    this.metadata = metadata ?? null
    this._feature = metadata?.feature
    this._handled = metadata?.handled ?? false

    // captureStackTrace is V8/Node-only; skip when unavailable (e.g. some browsers/runtimes)
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor)
    }
  }

  /**
   * Should the user be notified about this error?
   */
  shouldNotifyUser(): boolean {
    return false
  }

  /**
   * Should the error be reported to monitoring systems?
   */
  shouldReport(): boolean {
    return this._severity === 'critical' || this._severity === 'error'
  }

  /**
   * Vendor-neutral context handed to whichever error reporter is registered.
   *
   * Flat on purpose: observability backends index top-level attributes, so
   * nesting these under a `tags`/`extra` envelope would make them unsearchable.
   *
   * `originalError` is serialized rather than passed through, because
   * `JSON.stringify` emits an `Error` as `{}`. Its stack matters: the logger
   * wraps caught errors, and this class calls `Error.captureStackTrace`, so
   * *this* error's stack starts at the logger. The cause's stack is the only
   * record of where the failure actually came from.
   */
  toLogContext(): Record<string, unknown> {
    const { feature, operationName, originalError, ...rest } =
      this.metadata ?? {}

    return {
      errorName: this.name,
      severity: this._severity,
      handled: this._handled,
      ...(feature ? { feature } : {}),
      ...(operationName ? { key: operationName } : {}),
      ...(originalError instanceof Error
        ? {
            'error.cause.kind': originalError.name,
            'error.cause.message': originalError.message,
            'error.cause.stack': originalError.stack,
          }
        : {}),
      ...rest,
    }
  }

  set handled(handled: boolean) {
    this._handled = handled
  }

  get handled(): boolean {
    return this._handled
  }

  set feature(feature: string) {
    this._feature = feature
  }

  get feature(): string | undefined {
    return this._feature
  }

  set severity(severity: Severity) {
    this._severity = severity
  }

  get severity(): Severity {
    return this._severity
  }

  /**
   * Stable grouping key, emitted as `error.fingerprint`.
   *
   * Groups recurring failures together instead of opening a new issue per
   * unique id or email in the message — see `sanitizedMessage`.
   */
  get fingerprint(): string[] {
    const fingerprint = [this.name, this.sanitizedMessage()]

    if (this._severity) {
      fingerprint.push(this._severity)
    }

    if (this._feature) {
      fingerprint.push(this._feature)
    }

    if (this.metadata?.operationName) {
      fingerprint.push(this.metadata.operationName)
    }

    return fingerprint
  }

  private sanitizedMessage(): string {
    return (
      this.message
        // "rpc error: code = InvalidArgument desc = invalid email address" -> "invalid email address"
        .replace(/rpc error: code = \w+ desc = /gi, '')

        // "GenericError: no channels found for project uuid:"d02b..." -> "GenericError: no channels found for project uuid:"[uuid]""
        .replace(
          /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
          '[uuid]',
        )

        // "grpc: received message larger than max (7834202 vs. 4194304)" -> "grpc: received message larger than max ([size] vs. [max])"
        .replace(/\(\d+ vs\. \d+\)/g, '([size] vs. [max])')
        // Strip any other sensitive or dynamic data
        .replace(/[\w.-]+@[\w.-]+\.\w+/g, '[email]') // emails
        .replace(/\b\d{5,}\b/g, '[id]') // long numbers
        .trim()
    )
  }
}

/**
 * These errors are caused by user interactions and should be reported to the user
 * for feedback. Examples include validation errors, authentication errors, and other
 * user-related errors.
 */
export class UserError extends ApplicationError {
  constructor(message: string, metadata?: ErrorMetadata) {
    super(message, 'info', { ...metadata, handled: true })
    this.name = 'UserError'
  }

  override shouldNotifyUser(): boolean {
    return true
  }
}

/**
 * These errors are caused by server or network issues and typically can't be fixed by the user
 * and should be reported to the our monitoring systems for action.
 */
export class SystemError extends ApplicationError {
  private readonly transient: boolean

  constructor(message: string, metadata?: ErrorMetadata) {
    const handled = metadata?.handled ?? false
    const transient = metadata?.transient ?? false

    const severity = handled ? 'error' : 'critical'
    super(message, transient ? 'warning' : severity, metadata)
    this.name = 'SystemError'
    this.transient = transient
  }

  override shouldNotifyUser(): boolean {
    return !this.handled && !this.transient
  }

  get userMessage(): string {
    return this.metadata?.userMessage || this.message
  }

  set userMessage(message: string) {
    if (!this.metadata) {
      this.metadata = {}
    }

    this.metadata.userMessage = message
  }
}

export function isSystemError(error: unknown): error is SystemError {
  return error instanceof SystemError
}

export function isUserError(error: unknown): error is UserError {
  return error instanceof UserError
}
